import React, { RefObject, useState } from 'react';
import { LuDownload, LuPause, LuPlay } from 'react-icons/lu';

import { formatTranscriptOffset } from './call-utils';

const PLAYBACK_SPEEDS = [1, 1.5, 2];

/**
 * Inline player for a proxied call recording. The page owns the audio element ref
 * so transcript timestamps can seek into the recording.
 */
export const CallAudioPlayer = ({
    src,
    downloadUrl,
    audioRef,
    fallbackDurationSeconds,
}: {
    src: string;
    downloadUrl: string;
    audioRef: RefObject<HTMLAudioElement>;
    /** Duration from call metadata, shown until the audio metadata is loaded. */
    fallbackDurationSeconds?: number;
}) => {
    const [playing, setPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(fallbackDurationSeconds ?? 0);
    const [speed, setSpeed] = useState(1);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            audio.play();
        } else {
            audio.pause();
        }
    };

    const cycleSpeed = () => {
        const nextSpeed = PLAYBACK_SPEEDS[(PLAYBACK_SPEEDS.indexOf(speed) + 1) % PLAYBACK_SPEEDS.length];
        setSpeed(nextSpeed);
        if (audioRef.current) {
            audioRef.current.playbackRate = nextSpeed;
        }
    };

    const seek = (event: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * duration;
    };

    const progressPct = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

    return (
        <div className="border-border flex flex-shrink-0 items-center gap-3.5 border-b bg-[#F8F7F5] px-6 py-3">
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => {
                    if (Number.isFinite(e.currentTarget.duration)) {
                        setDuration(e.currentTarget.duration);
                    }
                }}
            />
            <button
                onClick={togglePlay}
                aria-label={playing ? 'Pause recording' : 'Play recording'}
                className="flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#2C2424] text-white hover:bg-[#433938]"
            >
                {playing ? (
                    <LuPause className="h-3.5 w-3.5 fill-current" />
                ) : (
                    <LuPlay className="h-3.5 w-3.5 fill-current" />
                )}
            </button>
            <span className="w-11 flex-shrink-0 text-xs text-[#5A4F4E] tabular-nums">
                {formatTranscriptOffset(elapsed * 1000)}
            </span>
            <div onClick={seek} className="relative h-1.5 flex-1 cursor-pointer rounded-full bg-[#CECAC6]">
                <div
                    className="absolute top-0 bottom-0 left-0 rounded-full bg-[#946A3E]"
                    style={{ width: `${progressPct}%` }}
                />
            </div>
            <span className="text-muted-foreground w-11 flex-shrink-0 text-xs tabular-nums">
                {formatTranscriptOffset(duration * 1000)}
            </span>
            <button
                onClick={cycleSpeed}
                className="border-border h-7 cursor-pointer rounded-full border bg-white px-2.5 text-xs text-[#5A4F4E] hover:bg-gray-50"
            >
                {speed}×
            </button>
            <a
                href={downloadUrl}
                download
                aria-label="Download recording"
                className="border-border flex h-7 w-7 items-center justify-center rounded-full border bg-white text-[#5A4F4E] hover:bg-gray-50"
            >
                <LuDownload className="h-3.5 w-3.5" />
            </a>
        </div>
    );
};
